import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import ProductName from "@/services/product-name";
import type { GetProductName, ProductNameDto } from "@/dtos/ProductName.dto";

export interface ProductNamesParams {
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

// The organization's catalog of distinct product names (backed by the `product_name`
// table), used to power the "select or create product name" dropdown on Add/Edit Product.
export function useProductNames(
  params: Ref<ProductNamesParams | null> | ProductNamesParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["products", "names", "disabled"];
    return ["products", "names", p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return !!p?.organizationId;
  });

  return useQuery<ProductNameDto[]>({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetProductName = {
        organizationId: p.organizationId,
      };

      const result = await ProductName.getProductNames(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load product names");
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
